import { TopDownItemId } from './config';

/**
 * URL-friendly slugs for each focusable item.
 * Used in clean URL paths like /rsvp, /faq, /schedule
 */
export const FOCUS_SLUGS = {
    [TopDownItemId.Schedule]: 'schedule',
    [TopDownItemId.Shoes]: 'dresscode',
    [TopDownItemId.Bowtie]: 'attire',
    [TopDownItemId.Matchbox]: 'faq',
    [TopDownItemId.ChromeTV]: 'gallery',
    [TopDownItemId.Envelope]: 'rsvp',
    [TopDownItemId.Hotels]: 'hotels',
    [TopDownItemId.RecordPlayer]: 'music',
    [TopDownItemId.Lilly]: 'lilly',
} as const;

export type FocusSlug = typeof FOCUS_SLUGS[keyof typeof FOCUS_SLUGS];

/**
 * Reverse mapping: slug → TopDownItemId
 */
const SLUG_TO_ITEM_ID: Record<string, TopDownItemId> = Object.entries(FOCUS_SLUGS).reduce(
    (acc, [itemId, slug]) => {
        acc[slug] = itemId as TopDownItemId;
        return acc;
    },
    {} as Record<string, TopDownItemId>
);

/**
 * Parse a focus item ID from the current URL path.
 * Supports formats:
 *   - /schedule
 *   - /rsvp
 * 
 * @returns The TopDownItemId if found, null otherwise
 */
export function parseFocusFromUrl(): TopDownItemId | null {
    const pathname = window.location.pathname;
    
    // Extract slug from path (e.g., "/rsvp" → "rsvp")
    const slug = pathname.replace(/^\/+/, '').toLowerCase().split('/')[0];
    if (!slug) return null;

    return SLUG_TO_ITEM_ID[slug] ?? null;
}

/**
 * Generate a URL path for linking to a specific focus state.
 * @param itemId The TopDownItemId to link to
 * @returns URL path like "/rsvp"
 */
export function getFocusUrl(itemId: TopDownItemId): string {
    const slug = FOCUS_SLUGS[itemId];
    return slug ? `/${slug}` : '/';
}

/**
 * Set the URL path to reflect the current focus state.
 * Uses replaceState to avoid polluting browser history during focus transitions.
 * @param itemId The focused item, or null to clear
 */
export function setFocusUrl(itemId: TopDownItemId | null): void {
    const newPath = itemId ? getFocusUrl(itemId) : '/';
    if (window.location.pathname !== newPath) {
        window.history.replaceState(null, '', newPath);
    }
}

/**
 * Navigate to a focus state, pushing to browser history.
 * Use this for user-initiated navigation (allows back button).
 * @param itemId The focused item to navigate to
 */
export function navigateToFocus(itemId: TopDownItemId): void {
    const newPath = getFocusUrl(itemId);
    if (window.location.pathname !== newPath) {
        window.history.pushState(null, '', newPath);
    }
}

/**
 * Clear the focus URL (navigate to root).
 */
export function clearFocusUrl(): void {
    setFocusUrl(null);
}

// Legacy aliases for backward compatibility
/** @deprecated Use parseFocusFromUrl */
export const parseFocusFromHash = parseFocusFromUrl;
/** @deprecated Use setFocusUrl */
export const setFocusHash = setFocusUrl;
/** @deprecated Use clearFocusUrl */
export const clearFocusHash = clearFocusUrl;
/** @deprecated Use getFocusUrl */
export function getFocusHash(itemId: TopDownItemId): string {
    return getFocusUrl(itemId);
}

